import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';

const PayForm = ({ data, allData, onSubmitForm }) => {
  // Check if subscription exists for a given year
  const isSubscriptionActive = (year) => {
    if (!allData?.subscriptions || !Array.isArray(allData.subscriptions)) {
      return false;
    }
    return allData.subscriptions.some(sub => sub.year == year);
  };

  // Get available years from subscriptions
  const getAvailableYears = () => {
    if (!allData?.subscriptions || !Array.isArray(allData.subscriptions)) {
      return [];
    }
    return allData.subscriptions.map(sub => sub.year);
  };

  // Get default date - only set if today's year is subscribed, otherwise use earliest subscribed year or empty
  const getDefaultDate = () => {
    const today = new Date();
    const todayYear = today.getFullYear();
    const availableYears = getAvailableYears();
    
    if (availableYears.length === 0) {
      return ''; // No subscriptions available
    }
    
    // If today's year is subscribed, use today's date
    if (isSubscriptionActive(todayYear)) {
      return today.toISOString().split('T')[0];
    }
    
    // Otherwise, use the earliest available year (January 1st of that year)
    const earliestYear = Math.min(...availableYears);
    return `${earliestYear}-01-01`;
  };

  const { register, handleSubmit, control, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      date: getDefaultDate()
    }
  });
  const [selectedCoas, setSelectedCoas] = useState([]);
  const [isDisabled, setIsDisabled] = useState(false);
  const invoiceValue = watch('invoice');
  const [selectedType, setSelectedType] = useState('');
  
  

  // Handle both single item and array of items
  const itemsArray = Array.isArray(data) ? data : [data]
  
  // Group items by coa_id and sum amounts for items with the same COA
  const coaMap = new Map()
  itemsArray.forEach((item) => {
    const coaId = item.coa_id || item.id
    if (coaMap.has(coaId)) {
      coaMap.set(coaId, {
        ...coaMap.get(coaId),
        amount: coaMap.get(coaId).amount + (parseFloat(item.amount) || 0)
      })
    } else {
      coaMap.set(coaId, {
        coa: coaId,
        label: allData.budget?.find(a => a.id === coaId)?.coa || 'Unknown COA',
        amount: parseFloat(item.amount) || 0
      })
    }
  })
  
  const filteredCoas = Array.from(coaMap.values())
  


  // Handle checkbox changes
  const handleCoaChange = (coaId, isChecked, defaultAmount) => {
    if (isChecked) {
      setSelectedCoas(prev => [...prev, { coa: coaId, amount: defaultAmount }]);
    } else {
      setSelectedCoas(prev => prev.filter(item => item.coa !== coaId));
    }
  };

  // Handle amount changes
  const handleAmountChange = (coaId, value) => {
    setSelectedCoas(prev =>
      prev.map(item =>
        item.coa === coaId ? { ...item, amount: Number(value) } : item
      )
    );
  };

  // Update form values when selectedCoas changes
  useEffect(() => {
    setValue('coa', selectedCoas);
  }, [selectedCoas, setValue]);

  // Clear COAs when invoice changes
  useEffect(() => {
    setSelectedCoas([]);
  }, [invoiceValue]);

  // Watch for type changes
  const typeValue = watch('type');
  useEffect(() => {
    setSelectedType(typeValue);
    if (typeValue === '2') {
      setValue('bank', '1');
    }
  }, [typeValue, setValue]);

  const onSubmit = async data => {
    
    if (data.coa.some(item => item.amount <= 0)) {
      toast.error('All COAs must have a positive amount');
      return;
    }
    if(data.type === '' ){
        toast.error('Please select type');
      return;
    }
    if(data.type === '2') {
      data.bank = '1';
    }
    setIsDisabled(true)
    await onSubmitForm(data)
    
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="max-w-2xl mx-auto p-4 space-y-6 ">
        <div className='grid grid-cols-2 gap-4'>
      
      {/* Date Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Date</label>
        <Controller
          name="date"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <>
              <input
                type="date"
                required
                {...field}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  const selectedYear = new Date(selectedDate).getFullYear();
                  
                  if (!isSubscriptionActive(selectedYear)) {
                    toast.error(`No active subscription found for year ${selectedYear}. Please select a date from a subscribed year.`);
                    return;
                  }
                  
                  field.onChange(e);
                }}
                className="w-full p-1 border rounded-md text-lg font-normal focus:outline-none focus:border-blue-950"
                min={getAvailableYears().length > 0 ? `${Math.min(...getAvailableYears())}-01-01` : undefined}
                max={getAvailableYears().length > 0 ? `${Math.max(...getAvailableYears())}-12-31` : undefined}
              />
              {field.value && !isSubscriptionActive(new Date(field.value).getFullYear()) && (
                <p className="text-red-500 text-sm mt-1">
                  No active subscription for {new Date(field.value).getFullYear()}. Available years: {getAvailableYears().join(', ') || 'None'}
                </p>
              )}
            </>
          )}
        />
        {errors.date && <span className="text-red-500 text-sm">This field is required</span>}
      </div>

      {/* COA Checkboxes with Amounts */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Chart of Accounts</label>
        <div className="mt-2 space-y-3">
          {filteredCoas.map((item) => (
            <div key={item.coa} className="flex items-center gap-4">
              <input
                type="checkbox"
                checked={selectedCoas.some(c => c.coa === item.coa)}
                onChange={(e) => handleCoaChange(item.coa, e.target.checked, item.amount)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="flex-1">
                {item.label}
              </label>
              {selectedCoas.some(c => c.coa === item.coa) && (
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={selectedCoas.find(c => c.coa === item.coa)?.amount || item.amount}
                  onChange={(e) => handleAmountChange(item.coa, e.target.value)}
                  className="w-32 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Amount"
                />
              )}
            </div>
          ))}
        </div>
        {errors.coa && <span className="text-red-500 text-sm">At least one COA must be selected</span>}
      </div>

  {/* Bank */}
  {selectedType !== '2' && allData.bank && allData.bank.length > 0 && (
    <div>
      <label className="block text-sm font-medium text-gray-700">Bank</label>
      <select
        required
        {...register('bank', { required: true })}
        className="w-full p-1 border rounded-md text-lg font-normal focus:outline-none focus:border-blue-950"
      >
        <option value="">Select Bank</option>
        {allData.bank.map((res) => (
          <option value={res.id}>{res.name}</option>
        ))}
      </select>
      {errors.bank && <span className="text-red-500 text-sm">This field is required</span>}
    </div>
  )}
     
 
      {/* Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Type</label>
        <select
        required
          {...register('type', { required: true })}
          className="w-full p-1 border rounded-md text-lg font-normal focus:outline-none focus:border-blue-950"
        >
          <option value="">Select Type</option>
          <option value="1">Payment</option>
          <option value="2">Credit note</option>
        </select>
        {errors.type && <span className="text-red-500 text-sm">This field is required</span>}
      </div>

      {/* Cheque Number */}
      {/* <div>
        <label className="block text-sm font-medium text-gray-700">Cheque #</label>
        <input
          type="text"
          {...register('cheque', { required: true })}
          className="w-full p-1 border rounded-md text-lg font-normal focus:outline-none focus:border-blue-950"
        />
        {errors.cheque && <span className="text-red-500 text-sm">This field is required</span>}
      </div> */}

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Description</label>
        <textarea
        required
          {...register('description', { required: true })}
          className="w-full p-1 border rounded-md text-lg font-normal focus:outline-none focus:border-blue-950"
          rows="3"
        />
        {errors.description && <span className="text-red-500 text-sm">This field is required</span>}
      </div>
      </div>
      <button
      disabled={isDisabled}
        type="submit"
        className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        Submit
      </button>
      
    </form>
  );
};

export default PayForm;